import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DEFAULT_BRAND_COLOR } from "@/lib/brand";
import { readRememberedPortal } from "@/lib/portal-memory";
import { useApplyBrand } from "@/hooks/use-apply-brand";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { NeutralFrame } from "@/components/shell/neutral-frame";

interface LoginSearch {
  redirect?: string;
}

/**
 * The bare entry. A remembered utility's page takes over, the redirect carried
 * along; with nothing remembered the page is NJBSoft's mark and one sentence,
 * which is the page's heading. It never lists, searches or offers a choice of
 * utilities.
 */
export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) throw redirect({ to: "/home" });
    const remembered = readRememberedPortal();
    if (remembered) {
      throw redirect({
        to: "/portal/$slug",
        params: { slug: remembered.slug },
        search: { redirect: search.redirect },
      });
    }
  },
  component: NeutralEntryPage,
});

function NeutralEntryPage() {
  const { t } = useTranslation("customer");
  // This route tree's one brand writer: the default brand, so an OS theme
  // change after load repaints it.
  useApplyBrand(DEFAULT_BRAND_COLOR);
  useDocumentTitle(null);

  return (
    <NeutralFrame>
      <h1 className="max-w-[36ch] text-center text-lg text-ink">
        {t("portal.neutral.instruction")}
      </h1>
    </NeutralFrame>
  );
}
